import { Icon } from '@iconify/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FileItem } from '@/Domain/components/KnowledgeBase/FileItem';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { AddFiles } from '@/Domain/components/icons/AddFiles.icon';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';
import { useUploadFile } from '@/Domain/hooks/App/useUploadFile.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { useUploadStore } from '@/Domain/store/upload.store';

type Props = {
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  onUpload: (files: File[], onComplete?: () => void) => void;
};

export const UploadArticlesModal = (props: Props) => {
  const { t } = useTranslation(['knowledge_base']);
  const { toggleModal, isOpen } = useModalStore();
  const { addItems } = useUploadStore();
  const isModalOpen = isOpen(MODAL_NAMES.UPLOAD_MODAL_ID);

  const {
    files,
    getRootProps,
    getInputProps,
    removeFile,
    getFileIcon,
    getFileSizeText,
    isPending,
  } = useUploadFile({
    ...props,
    open: isModalOpen,
    onOpenChange: () => toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID),
  });

  const handleHelpClick = () => {
    window.open('https://docs.quickchat.ai/ai-agent/knowledge-base/', '_blank');
  };

  const handleClose = () => toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID);

  const handleUploadAndClose = () => {
    const validFiles = files.filter((file) => !file.error);

    if (validFiles.length > 0) {
      validFiles.forEach((file) => {
        addItems(file, UploadItemTypeEnum.FILE);
      });

      toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID);
      props.onUpload(validFiles, () => {});
    } else {
      toggleModal(MODAL_NAMES.UPLOAD_MODAL_ID);
    }
  };

  return (
    <>
      <Dialog open={isModalOpen} onOpenChange={handleClose}>
        <DialogContent className="w-lg">
          <DialogHeader>
            <DialogTitle className="font-semibold">{t('modals.upload_file.title')}</DialogTitle>
            <DialogDescription>{t('modals.upload_file.description')}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-6">
            <div
              {...getRootProps()}
              className={classNames(
                'flex flex-col items-center justify-center w-full rounded-lg bg-muted px-6 py-8 text-center transition-colors duration-200',
              )}
            >
              <input {...getInputProps()} />
              <AddFiles className="-mb-11" />

              <div>
                <p className="mt-4 mb-2">
                  <span className="text-sm cursor-pointer underline decoration-1 underline-offset-2">
                    {t('modals.upload_file.select_files')}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {' '}
                    {t('modals.upload_file.or_drop_here')}
                  </span>
                </p>
                <p className="text-xs text-foreground-subtle">
                  {t('modals.upload_file.max_file_size', {
                    maxFileSize: props.maxFileSize || 40,
                  })}
                </p>
              </div>
            </div>

            {files.length > 0 && (
              <div className="max-h-52 overflow-y-auto custom-scroll">
                <ul className="space-y-3 list-none">
                  {files.map((fileObj, index) => (
                    <li key={`upload-file-${index}-${fileObj.name}`}>
                      <FileItem
                        name={fileObj.name}
                        size={fileObj.size}
                        error={fileObj.error}
                        status={isPending ? UploadStatusEnum.PENDING : UploadStatusEnum.DONE}
                        index={index}
                        onRemove={removeFile}
                        getFileIcon={getFileIcon}
                        getFileSizeText={getFileSizeText}
                        showStatus={false}
                        showRemoveButton={true}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Separator className="bg-accent mt-2" />

          <DialogFooter className="!flex !flex-row !justify-between !items-center gap-2 mt-2">
            <div
              className="flex items-center justify-start cursor-pointer"
              onClick={handleHelpClick}
            >
              <Icon
                icon="mingcute:question-line"
                className="h-5 w-5 text-foreground-subtle group-hover:text-muted-foreground mr-2"
              />
              <span className="text-muted-foreground text-sm">
                {t('modals.upload_file.need_help')}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleClose}>
                {t('modals.upload_file.cancel')}
              </Button>
              <Button
                variant="dark"
                onClick={handleUploadAndClose}
                disabled={files.length === 0 || files.every((file) => !!file.error) || isPending}
              >
                {isPending
                  ? `${t('modals.upload_file.uploading')} `
                  : t('modals.upload_file.upload')}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
